import { RootState } from '../../core/store';
import { createSelector } from '@reduxjs/toolkit';
import { TPhaseCountsOfGeneralSeaAreas, TPhasePercentages, TSignalCountsOnSpeedRanges, TKeyMetrics, TVesselSpeedDraughtValues, TSummaryKeyMetrics, TSummaryPhaseCountsOfGeneralSeaAreas, TSummaryPhasePercentages, TSummarySignalCountsOnSpeedRanges, TPolygonCalls, TVoyages, TStoppageEvents, IKpiVessel, TCountsOfGeneralSeaAreas, TPhaseCountsOfGeneralSeaAreasItem } from './kpiTypes';
import { selectSubscribedMsiVessels } from '../../user-management/appSelectors';
import { selectSelectionData } from '../search/searchSelectors';

const selectKpiSlice = (state: RootState) => state.kpiSlice;

export const kpiVesselListSelector = createSelector(
    [selectSubscribedMsiVessels, selectSelectionData],
    (subscribedMsiVessels, selectionData) => {
        const selectedImoNumbers = new Set(selectionData.selectedVesselsList.map(v => v.imoNumber));
        return subscribedMsiVessels.filter(v => selectedImoNumbers.has(v.imoNumber)).map(v => ({ imoNumber: v.imoNumber, name: v.vesselName, sectorId: v.sectorId, sectorName: v.sectorName })) as IKpiVessel[];
    }
);

export const summaryKeyMetricsInfoSelector = createSelector(
    selectKpiSlice,
    (kpiSlice) => kpiSlice.summaryKeyMetrics as TSummaryKeyMetrics
);

export const summaryPhaseCountsOfGeneralSeaAreasSelector = createSelector(
    selectKpiSlice,
    (kpiSlice) => kpiSlice.summaryPhaseCountsOfGeneralSeaAreas as TSummaryPhaseCountsOfGeneralSeaAreas
);

export const summaryPhasePercentagesSelector = createSelector(
    selectKpiSlice,
    (kpiSlice) => kpiSlice.summaryPhasePercentages as TSummaryPhasePercentages
);

export const summarySignalCountsOnSpeedRangesSelector = createSelector(
    selectKpiSlice,
    (kpiSlice) => kpiSlice.summarySignalCountsOnSpeedRanges as TSummarySignalCountsOnSpeedRanges
);

export const phasePercentagesSelector = createSelector(
    selectKpiSlice,
    (_: RootState, imoNumber: number) => imoNumber,
    (kpiSlice, imoNumber) => {
        return (kpiSlice.vesselListPhasePercentages?.find((p) => p.imoNumber === imoNumber) ?? null) as TPhasePercentages;
    }
);

export const phaseCountsOfGeneralSeaAreasSelector = createSelector(
    selectKpiSlice,
    (_: RootState, imoNumber: number) => imoNumber,
    (kpiSlice, imoNumber) => {
        return (kpiSlice.vesselListPhaseCountsOfGeneralSeaAreas?.find((p) => p.imoNumber === imoNumber) ?? null) as TPhaseCountsOfGeneralSeaAreas;
    }
);

export const signalCountsOnSpeedRangesSelector = createSelector(
    selectKpiSlice,
    (_: RootState, imoNumber: number) => imoNumber,
    (kpiSlice, imoNumber) => {
        return (kpiSlice.vesselListSignalCountsOnSpeedRanges?.find((p) => p.imoNumber === imoNumber) ?? null) as TSignalCountsOnSpeedRanges;
    }
);

export const keyMetricsInfoSelector = createSelector(
    selectKpiSlice,
    (_: RootState, imoNumber: number) => imoNumber,
    (kpiSlice, imoNumber) => {
        return (kpiSlice.vesselListKeyMetrics?.find((p) => p.imoNumber === imoNumber) ?? null) as TKeyMetrics;
    }
);

export const vesselSpeedAndDraughtValuesSelector = createSelector(
    selectKpiSlice,
    (_: RootState, imoNumber: number) => imoNumber,
    (kpiSlice, imoNumber) => {
        return (kpiSlice.vesselListSpeedDraughtValues?.find((p) => p.imoNumber === imoNumber) ?? null) as TVesselSpeedDraughtValues;
    }
);

export const vesselPolygonCallsSelector = createSelector(
    selectKpiSlice,
    (_: RootState, imoNumber: number) => imoNumber,
    (kpiSlice, imoNumber) => {
        return (kpiSlice.vesselListPolygonCalls?.find((p) => p.imoNumber === imoNumber) ?? null) as TPolygonCalls;
    }
);

export const vesselVoyagesSelector = createSelector(
    selectKpiSlice,
    (_: RootState, imoNumber: number) => imoNumber,
    (kpiSlice, imoNumber) => {
        return (kpiSlice.vesselListVoyages?.find((p) => p.imoNumber === imoNumber) ?? null) as TVoyages;
    }
);

export const vesselStoppageEventsSelector = createSelector(
    selectKpiSlice,
    (_: RootState, imoNumber: number) => imoNumber,
    (kpiSlice, imoNumber) => {
        return (kpiSlice.vesselListStoppageEvents?.find((p) => p.imoNumber === imoNumber) ?? null) as TStoppageEvents;
    }
);

export const vesselCohortVoyagesSelector = createSelector(
    selectKpiSlice,
    (_: RootState, imoNumber: number) => imoNumber,
    (kpiSlice, imoNumber) => {
        return (kpiSlice.vesselCohortVoyages?.imoNumber === imoNumber ? kpiSlice.vesselCohortVoyages : null) as TVoyages;
    }
);

export const vesselCohortSignalCountsOnSpeedRangesSelector = createSelector(
    selectKpiSlice,
    (_: RootState, imoNumber: number) => imoNumber,
    (kpiSlice, imoNumber) => {
        return (kpiSlice.vesselCohortSignalCountsOnSpeedRanges?.imoNumber === imoNumber ? kpiSlice.vesselCohortSignalCountsOnSpeedRanges : null) as TSignalCountsOnSpeedRanges;
    }
);

export const vesselCohortPhasePercentagesOfPolygonCallsSelector = createSelector(
    selectKpiSlice,
    (_: RootState, imoNumber: number) => imoNumber,
    (kpiSlice, imoNumber) => {
        return (kpiSlice.vesselCohortPhasePercentagesOfPolygonCalls?.imoNumber === imoNumber ? kpiSlice.vesselCohortPhasePercentagesOfPolygonCalls : null) as TPhasePercentages;
    }
);

export const selectCountsOfGeneralSeaAreasForVessel = createSelector(
    selectKpiSlice,
    (_: RootState, imoNumber: number) => imoNumber,
    (kpiSlice, imoNumber) => {
        return (processSeaAreaData(kpiSlice.vesselListPhaseCountsOfGeneralSeaAreas?.find((p) => p.imoNumber === imoNumber)?.dataList ?? null));
    }
);

export const selectSeaAreasVesselLoading = createSelector(
    selectKpiSlice,
    (_: RootState, imoNumber: number) => imoNumber,
    (kpiSlice, imoNumber) => {
        const entry = kpiSlice.vesselListPhaseCountsOfGeneralSeaAreas?.find((p) => p.imoNumber === imoNumber);
        return entry ? entry.loading : true;
    }
);

export const selectCountsOfGeneralSeaAreasForCohort = createSelector(
    selectKpiSlice,
    (kpiSlice) => {
        if (kpiSlice.summaryPhaseCountsOfGeneralSeaAreas && kpiSlice.summaryPhaseCountsOfGeneralSeaAreas.sectorListPhaseCountsOfGeneralSeaAreas) {
            return processSeaAreaData(kpiSlice.summaryPhaseCountsOfGeneralSeaAreas.sectorListPhaseCountsOfGeneralSeaAreas[0].dataList);
        }
        return [];
    }
);

export const selectSeaAreasCohortLoading = createSelector(
    selectKpiSlice,
    (kpiSlice) => kpiSlice.summaryPhaseCountsOfGeneralSeaAreas?.loading ?? true
);

const processSeaAreaData = (countsOfGeneralSeaAreas: TPhaseCountsOfGeneralSeaAreasItem[] | null): TCountsOfGeneralSeaAreas[] => {
    if (!countsOfGeneralSeaAreas || countsOfGeneralSeaAreas.length === 0) return [];

    const data = countsOfGeneralSeaAreas;
    // Calculate total phases for each area
    const processedData = data.map((area) => {
        const totalPhases =
            area.countOfPhaseAtSea + area.countOfPhaseAtBerth + area.countOfPhaseAnchored + area.countOfPhaseManoeuvring;
        return { generalSeaArea: area.generalSeaArea, totalPhases };
    });

    // Calculate overall total of all phases
    const overallTotal = processedData.reduce((sum, area) => sum + area.totalPhases, 0);

    // Calculate percentage of total
    return processedData.map((area) => ({
        ...area,
        percentageOfTotal: overallTotal > 0 ? (area.totalPhases / overallTotal) * 100 : 0,
    })).filter((area) => area.totalPhases > 0).sort((a, b) => b.totalPhases - a.totalPhases);
};

export const selectVesselVoyagesHourlyTracks = createSelector(
    (selectKpiSlice),
    (kpiSlice) => kpiSlice.vesselVoyagesHourlyTracks);

export const selectActivityCoverage = createSelector(
    (selectKpiSlice),
    (kpiSlice) => kpiSlice.activityCoverage);

export const selectSectorActivityCoverage = createSelector(
    (selectKpiSlice),
    (kpiSlice) => kpiSlice.sectorActivityCoverage);